import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchRoute, RouteData, RouteStep } from '../../lib/routing';
import { Loader2, MapPin as MapPinIcon } from 'lucide-react';
interface StaticRouteMapProps {
  destinationLat: number;
  destinationLng: number;
  onRouteData?: (data: {
    distance: number;
    duration: number;
    steps: RouteStep[];
  } | null) => void;
}
// All hooks MUST be at the top — no conditional returns before hooks
const driverIcon = L.divIcon({
  className: '',
  html: '<div style="width:18px;height:18px;background:#3b82f6;border:3px solid white;border-radius:50%;box-shadow:0 0 8px rgba(59,130,246,0.6);"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9]
});
const destIcon = L.divIcon({
  className: '',
  html: '<div style="width:22px;height:22px;background:#00a870;border:3px solid white;border-radius:50%;box-shadow:0 0 8px rgba(0,168,112,0.6);display:flex;align-items:center;justify-content:center;"><div style="width:7px;height:7px;background:white;border-radius:50%;"></div></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11]
});
function FitBounds({
  driverPos,
  destPos,
  routeCoords
}: {
  driverPos: [number, number] | null;
  destPos: [number, number];
  routeCoords: [number, number][];
}) {
  const map = useMap();
  useEffect(() => {
    if (routeCoords.length > 0) {
      map.fitBounds(L.latLngBounds(routeCoords), {
        padding: [60, 60],
        maxZoom: 17
      });
    } else if (driverPos) {
      map.fitBounds(L.latLngBounds([driverPos, destPos]), {
        padding: [60, 60],
        maxZoom: 16
      });
    } else {
      map.setView(destPos, 15);
    }
  }, [map, driverPos, destPos, routeCoords]);
  return null;
}
export function StaticRouteMap({
  destinationLat,
  destinationLng,
  onRouteData
}: StaticRouteMapProps) {
  // ALL hooks at the top — never after a conditional return
  const [driverPos, setDriverPos] = useState<[number, number] | null>(null);
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const destPos: [number, number] = [destinationLat, destinationLng];
  useEffect(() => {
    let cancelled = false;
    async function init() {
      // Step 1: Get driver position (one-time)
      if (!navigator.geolocation) {
        if (!cancelled) {
          setErrorMsg('GPS no disponible');
          setStatus('error');
        }
        return;
      }
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000
          });
        });
        if (cancelled) return;
        const pos: [number, number] = [position.coords.latitude, position.coords.longitude];
        setDriverPos(pos);
        // Step 2: Fetch route (one-time)
        const result = await fetchRoute(pos[0], pos[1], destinationLat, destinationLng);
        if (cancelled) return;
        if (result && !('error' in result)) {
          setRouteData(result);
          setStatus('ready');
          onRouteData?.({
            distance: result.distance,
            duration: result.duration,
            steps: result.steps
          });
          console.log('[STATIC-ROUTE] ✅ Route loaded:', {
            distance: result.distance,
            duration: result.duration,
            steps: result.steps.length
          });
        } else {
          // Route failed but we still have driver position — show markers without polyline
          console.warn('[STATIC-ROUTE] ⚠️ Route fetch failed, showing markers only');
          onRouteData?.(null);
          setStatus('ready');
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('[STATIC-ROUTE] ❌ GPS error:', err.message);
          setErrorMsg('No se pudo obtener tu ubicación');
          setStatus('error');
        }
      }
    }
    init();
    return () => {
      cancelled = true;
    };
  }, [destinationLat, destinationLng]);
  // Render — no early returns, all states handled in one tree
  return <div className="w-full h-full rounded-2xl overflow-hidden border border-white/10 bg-[#1A1A1A] relative" data-id="element-1578">
      {status === 'loading' && <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#1A1A1A]" data-id="element-1579">
          <Loader2 className="w-8 h-8 text-[#00a870] animate-spin mb-3" data-id="element-1580" />
          <p className="text-white/40 text-sm font-bold" data-id="element-1581">Cargando mapa...</p>
        </div>}

      {status === 'error' && <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#1A1A1A]" data-id="element-1582">
          <MapPinIcon className="w-10 h-10 text-white/15 mb-3" data-id="element-1583" />
          <p className="text-white/40 text-sm font-bold" data-id="element-1584">
            {errorMsg || 'Error cargando mapa'}
          </p>
        </div>}

      {/* Always render MapContainer to avoid hook issues — hide when not ready */}
      <div className={status === 'ready' ? 'w-full h-full' : 'w-full h-full opacity-0 pointer-events-none'} data-id="element-1585">
        <MapContainer center={destPos} zoom={15} className="w-full h-full" zoomControl={false} data-id="element-1586">
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="&copy; OpenStreetMap &copy; CARTO" data-id="element-1587" />

          <FitBounds driverPos={driverPos} destPos={destPos} routeCoords={routeData?.coordinates || []} data-id="element-1588" />

          {/* Route polyline shadow */}
          {routeData && <Polyline positions={routeData.coordinates} color="#000000" weight={8} opacity={0.3} lineCap="round" lineJoin="round" data-id="element-1589" />}

          {/* Route polyline */}
          {routeData && <Polyline positions={routeData.coordinates} color="#00a870" weight={6} opacity={0.9} lineCap="round" lineJoin="round" data-id="element-1590" />}

          {/* Driver marker */}
          {driverPos && <Marker position={driverPos} icon={driverIcon} data-id="element-1591" />}

          {/* Destination marker */}
          <Marker position={destPos} icon={destIcon} data-id="element-1592" />
        </MapContainer>
      </div>
    </div>;
}